/**
 * DeviceImport — Network-wide > Configure > Import from Workbook.
 *
 * Loads device names and PHYSICAL PLACEMENT from the install workbook. Placement is the
 * reason this screen exists: a hostname convention like SITE-BLDG1-LAUNDRY-AP008 needs
 * to know the device sits in the laundry, and nothing reaching the controller at check-in
 * knows that. A device presents a serial and a MAC. The installer's spreadsheet is the only
 * source, so until it is imported the {loc} token renders empty for the whole fleet.
 *
 * The screen is a REVIEW-THEN-COMMIT flow, not a file upload. An import touches every device
 * at a site at once and one column mapped wrong renames an entire site, so the plan is
 * the main surface and applying is the smaller, second act. Nothing is written until the
 * operator has seen, per row, exactly what would change.
 */
import { useRef, useState } from "react";
import { useImportDevices } from "@/lib/api/hooks";
import { PageHeader, ErrorBanner } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";
import type { ImportResult } from "@/types/domain";

/* Example CSV for the "Use an example" button and the textarea placeholder. Deliberately
   FICTITIOUS (UI review 2026-10-17): the previous sample used a real customer's hostname
   convention and two real AP serials, and it is shown to every customer. The serials are
   locally administered MACs (02:00:00:...), which no shipped device carries, and the names
   are an obviously generic SITE-BLDG-FLR-AP### pattern. */
const SAMPLE = `serial,name,location
020000000001,SITE-BLDG1-FL1-AP001,LOBBY
020000000002,SITE-BLDG1-FL2-AP002,ROOM-201`;

export default function DeviceImport() {
  const run = useImportDevices();
  const fileRef = useRef<HTMLInputElement>(null);
  const [csv, setCsv] = useState("");
  const [plan, setPlan] = useState<ImportResult | null>(null);
  const [applied, setApplied] = useState<ImportResult | null>(null);

  /* A new file or a new paste invalidates any plan on screen. Leaving a stale plan visible
     next to changed input is how someone applies the previous file. */
  const replaceCsv = (text: string) => {
    setCsv(text);
    setPlan(null);
    setApplied(null);
  };

  const readFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => replaceCsv(String(reader.result ?? ""));
    reader.readAsText(file);
  };

  const preview = () =>
    run.mutate({ csv }, { onSuccess: (res) => { setPlan(res); setApplied(null); } });

  const apply = () =>
    run.mutate({ csv, apply: true }, { onSuccess: (res) => { setApplied(res); setPlan(null); } });

  const result = applied ?? plan;

  return (
    <div>
      <PageHeader
        title="Import from Workbook"
        subtitle="Set device names and where each one is installed, from the installer's spreadsheet"
      />

      <div className="flex flex-col gap-4">
        <section className="md-card p-4">
          <h2 className="text-sm font-medium text-ink">1. The file</h2>
          <p className="mt-1 text-sm text-ink-muted">
            A CSV with a <span className="font-mono text-ink">serial</span> column, plus{" "}
            <span className="font-mono text-ink">name</span> and/or{" "}
            <span className="font-mono text-ink">location</span>. Other columns are ignored, and
            common spellings work — <span className="font-mono">Serial Number</span>,{" "}
            <span className="font-mono">Room</span>, <span className="font-mono">IDF</span>.
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv,text/plain"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) readFile(file);
                e.target.value = "";
              }}
            />
            <Button variant="secondary" onClick={() => fileRef.current?.click()}>
              Choose a file
            </Button>
            <Button variant="ghost" onClick={() => replaceCsv(SAMPLE)}>
              Use an example
            </Button>
          </div>

          <label className="mt-3 flex flex-col gap-1 text-sm">
            <span className="text-ink-muted">…or paste it here</span>
            <textarea
              value={csv}
              onChange={(e) => replaceCsv(e.target.value)}
              rows={7}
              spellCheck={false}
              placeholder={SAMPLE}
              className="rounded border border-line bg-surface px-3 py-2 font-mono text-xs text-ink outline-none focus:border-brand"
            />
          </label>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {/* The dry run is a POST too (/devices/import, apply:false), so the server's global
                write floor (site_operator, security/authz.enforce_baseline_role) refuses it for
                read-only users with a 403. Disabled with the reason (UI review 2026-10-17). */}
            <RoleGate min="site_operator" mode="disable" reason="Checking a file requires a site manager or above.">
              <Button variant="primary" onClick={preview} disabled={!csv.trim() || run.isPending}>
                {run.isPending && !applied ? "Checking…" : "Check the file"}
              </Button>
            </RoleGate>
            {csv && (
              <Button variant="ghost" onClick={() => replaceCsv("")}>
                Clear
              </Button>
            )}
          </div>

          {run.isError && (
            <div className="mt-3">
              <ErrorBanner error={run.error} />
            </div>
          )}
        </section>

        {result && <PlanPanel result={result} onApply={apply} pending={run.isPending} />}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------------------- */

function PlanPanel({
  result,
  onApply,
  pending,
}: {
  result: ImportResult;
  onApply: () => void;
  pending: boolean;
}) {
  const { summary, items, applied } = result;
  const changing = items.filter((i) => i.outcome === "update");
  const unknown = items.filter((i) => i.outcome === "unknown");

  return (
    <section className="md-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-medium text-ink">
            {applied ? "3. Done" : "2. What this would change"}
          </h2>
          <p className="mt-1 text-sm text-ink-muted">
            {applied
              ? `Updated ${result.changed?.length ?? 0} device${
                  (result.changed?.length ?? 0) === 1 ? "" : "s"
                }.`
              : `${summary.rows} row${summary.rows === 1 ? "" : "s"} read. Nothing has been changed yet.`}
          </p>
        </div>
        {!applied && (
          <RoleGate min="site_operator" mode="disable" reason="You cannot change devices at this scope.">
            <Button variant="primary" onClick={onApply} disabled={pending || summary.update === 0}>
              {pending ? "Applying…" : `Apply to ${summary.update} device${summary.update === 1 ? "" : "s"}`}
            </Button>
          </RoleGate>
        )}
      </div>

      {/* Counts first: the operator's question is "does this look about right?", which is a
          question about magnitude before it is one about any individual row. */}
      <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
        <Count label={applied ? "Changed" : "To change"} value={summary.update} emphasis />
        <Count label="Already correct" value={summary.unchanged} />
        <Count label="Not found here" value={summary.unknown} warn={summary.unknown > 0} />
      </dl>

      {summary.unknown > 0 && (
        <p className="mt-2 text-xs text-ink-muted">
          Rows marked “not found here” list a serial this controller does not have in the
          sites you can see — usually kit that has not been delivered or plugged in yet.
          They were skipped, and re-importing later will pick them up.
        </p>
      )}

      {changing.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[34rem] border-collapse text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-ink-subtle">
                <th className="py-1.5 pr-3 font-medium">Serial</th>
                <th className="py-1.5 pr-3 font-medium">Name</th>
                <th className="py-1.5 font-medium">Installed at</th>
              </tr>
            </thead>
            <tbody>
              {changing.map((item) => (
                <tr key={item.serial} className="border-b border-line/60 last:border-0">
                  <td className="py-1.5 pr-3 font-mono text-xs text-ink-muted">{item.serial}</td>
                  <td className="py-1.5 pr-3">
                    <Delta change={item.changes.hostname} />
                  </td>
                  <td className="py-1.5">
                    <Delta change={item.changes.location} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {unknown.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs text-ink-muted">
            Show the {unknown.length} skipped row{unknown.length === 1 ? "" : "s"}
          </summary>
          <ul className="mt-2 flex flex-col gap-0.5 font-mono text-xs text-ink-subtle">
            {unknown.map((item) => (
              <li key={item.serial}>{item.serial}</li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

function Count({
  label,
  value,
  emphasis,
  warn,
}: {
  label: string;
  value: number;
  emphasis?: boolean;
  warn?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-1.5">
      <dt className="text-xs uppercase tracking-wide text-ink-subtle">{label}</dt>
      <dd
        className={
          warn
            ? "font-medium tabular-nums text-warn"
            : emphasis
              ? "font-medium tabular-nums text-ink"
              : "tabular-nums text-ink-muted"
        }
      >
        {value}
      </dd>
    </div>
  );
}

/** old → new, with the old value struck through. Unchanged fields render as a quiet dash. */
function Delta({ change }: { change?: { from: string | null; to: string } }) {
  if (!change) return <span className="text-ink-subtle">—</span>;
  return (
    <span className="flex flex-wrap items-baseline gap-1.5">
      {change.from ? (
        <span className="text-xs text-ink-subtle line-through">{change.from}</span>
      ) : (
        <span className="text-xs text-ink-subtle">(blank)</span>
      )}
      <span aria-hidden className="text-ink-subtle">
        →
      </span>
      <span className="font-mono text-xs text-ink">{change.to}</span>
    </span>
  );
}
